import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { dequal as deepEqual } from 'dequal/lite';
import { useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { maxManagedLighthousesQueryOptions } from '#api/managed-lighthouses.queries.ts';
import { networksQueryOptions } from '#api/networks.queries.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { AlertIcon, CheckCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { AddButton } from '#components/atoms/buttons/add-button/AddButton.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { Checkbox } from '#components/atoms/forms/checkbox/Checkbox.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage, HelpText, Input, Label } from '#components/atoms/forms/index.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { FieldSet } from '#components/patterns/forms/fieldset/FieldSet.tsx';
import { AssignedIPv6NetworkSize } from '#components/patterns/network/AssignedIPv6NetworkSize.tsx';
import { NetworkSize } from '#components/patterns/network/NetworkSize.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { useUnknownConfigOverrides } from '#hooks/useUnknownConfigOverrides.ts';
import { RESTART_REQUIRED_SETTINGS_DEFAULT } from '#screens/hosts/form/advanced/form-data.ts';
import { BlockNavigationDialog } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { AddConfigOverrideModal } from '#screens/tags/form/config-overrides/AddConfigOverrideModal.tsx';
import { EditConfigOverrideModal } from '#screens/tags/form/config-overrides/EditConfigOverrideModal.tsx';
import { ConfigOverridesList } from '#screens/tags/form/config-overrides/override-list-form/ConfigOverridesList.tsx';
import { mergeAllowListOverrides } from '#screens/tags/form/util/merge-allow-list-overrides.ts';
import { isNetworkMtlhEligible } from '#util/managedLighthouses.ts';
import { mapAPIListErrorsToFormListErrors } from '#util/map-api-list-errors-to-form-list-errors.ts';
import { makeIsEditableConfigOverrideKey, withUnknownOverrides } from '#util/unknownConfigOverrides.ts';
import { detectNetworkOverlap } from './detectNetworkOverlap.ts';
import { DisableManagedLighthousesWarning } from './DisableManagedLighthousesWarning.tsx';
import { useAddableCidrV4 } from './hooks/useAddableCidrV4.ts';
import { networkFormSchema, type NetworkFormData } from './network-form-schema.ts';
import { useAddNetwork, useEditNetwork, useSetNetworkConfigOverrides } from './NetworkForm.queries.ts';
import { RestartNetworkModal } from './RestartNetworkModal.tsx';
import styles from './NetworkForm.module.css';

type EditProps = {
  networkId: `network-${string}`;
  certVersion: 1 | 2;
  /** Used to decide whether changed config overrides warrant a restart-required notice */
  hostCount: number;
  defaultValues: NetworkFormData;
  onSuccess: () => void;
  /** Opens the cert v2 update, which is what makes managed lighthouses available to a cert v1 network */
  onOpenUpdate?: () => void;
};

type AddProps = {
  networkId: null;
  onSuccess: () => void;
};

type Props = EditProps | AddProps;

const DEFAULT_VALUES: NetworkFormData = {
  networkName: '',
  description: '',
  cidrV4: '',
  cidrV6: '',
  lighthousesAsRelays: true,
  curveP256: false,
  useManagedLighthouses: true,
  configOverrides: [],
};

/** Overrides have unique keys (the Add modal filters out existing ones), so key order is total. */
function sortByKey(overrides: ConfigOverride[]) {
  return [...overrides].sort((a, b) => a.key.localeCompare(b.key));
}

export function NetworkForm(props: Props) {
  const isEditing = props.networkId !== null;
  const savedValues = isEditing ? props.defaultValues : DEFAULT_VALUES;
  const { data: { DNS_CONFIG_OVERRIDES } = {} } = useSuspenseFeatureFlagsQuery();
  // Overrides with no editor here would fail the form's discriminated-union parse on save.
  // Keep them out of the form; they render read-only and re-attach on submit unless deleted.
  const {
    knownOverrides,
    unknownOverrides,
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  } = useUnknownConfigOverrides(
    savedValues.configOverrides,
    makeIsEditableConfigOverrideKey(Boolean(DNS_CONFIG_OVERRIDES))
  );
  const defaultValues = useMemo(
    () => ({ ...savedValues, configOverrides: knownOverrides }),
    [savedValues, knownOverrides]
  );
  const { data: { data: networks = [] } = {} } = useQuery(networksQueryOptions);
  // Overrides can only be saved against an existing network, so the section is edit-only.
  const showConfigOverrides = isEditing;
  // Filter out this network from the overlap check. This is safe because existing CIDRs are
  // readonly and a v6-only network has no IPv4 to conflict with itself when adding one. If we ever
  // allow editing an existing CIDR, this should depend on form state instead.
  const filteredNetworks = isEditing ? networks.filter(({ id }) => id !== props.networkId) : networks;

  // v1 networks can't run managed lighthouses (MTLH is IPv6-only; v1 networks are IPv4-only). New
  // networks are always created as v2, so this only bites when editing a legacy v1 network.
  const managedLighthousesApplicable = props.networkId === null || isNetworkMtlhEligible(props);

  const [isCommitting, setIsCommitting] = useState(false);
  const [confirmDisableMtlh, setConfirmDisableMtlh] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const editNetworkMutation = useEditNetwork();
  const addNetworkMutation = useAddNetwork();
  const setConfigOverridesMutation = useSetNetworkConfigOverrides();
  const { addCidrIfNeeded, lockedCidrV4 } = useAddableCidrV4({
    networkId: props.networkId,
    existingCidrV4: isEditing && props.defaultValues.cidrV4 ? props.defaultValues.cidrV4 : undefined,
  });

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    control,
    formState: { isDirty, errors },
  } = useForm({
    resolver: zodResolver(networkFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    fields: configOverrides,
    remove: removeConfigOverrides,
    append: appendConfigOverrides,
    update: updateConfigOverride,
  } = useFieldArray({
    control,
    name: 'configOverrides',
  });
  const [isAddingConfigOverride, setIsAddingConfigOverride] = useState<boolean>(false);
  const [editConfigOverrideIndex, setEditConfigOverrideIndex] = useState<number | null>(null);

  // Guard navigation while the form holds unsaved changes, but never while a save is in flight:
  // the success path navigates away, and an armed guard would cancel its own redirect.
  const blockNavigation = (isDirty || hasRemovedUnknownOverrides) && !isCommitting;

  const commitChanges = async (data: NetworkFormData) => {
    setIsCommitting(true);
    const { networkId, onSuccess } = props;
    try {
      if (networkId) {
        // Step 1: add IPv4 CIDR if the user filled one in on a v6-only network. Run first so
        // overlap/validation errors surface before we commit the metadata rename.
        await addCidrIfNeeded(data.cidrV4);
        // Step 2: metadata
        await editNetworkMutation.mutateAsync({
          id: networkId,
          values: {
            name: data.networkName,
            description: data.description,
            lighthousesAsRelays: data.lighthousesAsRelays,
            ...(managedLighthousesApplicable && { disableManagedLighthouses: !data.useManagedLighthouses }),
          },
        });
        // Step 3: config overrides, via their own full-replace endpoint. Only PUT when the set
        // actually changed — the API dirties every host in the network on each write. Compare
        // known overrides order-insensitively (removing and re-adding an override moves it to
        // the end of the field array); the unknown bucket only changes by deletion.
        if (showConfigOverrides) {
          const mergedOverrides = mergeAllowListOverrides(data.configOverrides);
          const savedOverrides = mergeAllowListOverrides(knownOverrides);
          const knownOverridesChanged = !deepEqual(sortByKey(mergedOverrides), sortByKey(savedOverrides));
          if (knownOverridesChanged || hasRemovedUnknownOverrides) {
            await setConfigOverridesMutation.mutateAsync({
              id: networkId,
              overrides: withUnknownOverrides(mergedOverrides, unknownOverrides),
            });
            // Has the user changed a setting that requires a restart? If so, let the user know.
            // NOTE: this does not take into account the version of dnclient on the hosts, and may
            // give false positives
            if (
              props.hostCount > 0 &&
              RESTART_REQUIRED_SETTINGS_DEFAULT.some((key) => {
                return !deepEqual(
                  mergedOverrides.find((c) => c.key === key),
                  savedOverrides.find((c) => c.key === key)
                );
              })
            ) {
              // onSuccess is called when the modal closes
              setShowRestartModal(true);
              return;
            }
          }
        }
      } else {
        await addNetworkMutation.mutateAsync({
          name: data.networkName,
          description: data.description,
          cidrs: data.cidrV4 ? [data.cidrV4] : [],
          lighthousesAsRelays: data.lighthousesAsRelays,
          curve: data.curveP256 ? 'P256' : '25519',
          disableManagedLighthouses: !data.useManagedLighthouses,
        });
      }

      onSuccess();
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          // Create path (POST /v2/networks) reports CIDR errors under cidrs[0] since we send the
          // whole CIDR array; add-CIDR path (POST /v2/networks/:id/cidrs) reports under `cidr`.
          if (path === 'cidrs[0]' || path === 'cidr') {
            setError('cidrV4', { message }, { shouldFocus: true });
            return true;
          }
          // The config-overrides endpoint reports errors under overrides[i]; our form field is
          // configOverrides. Indices can drift when allow lists are involved (the form splits
          // them one rule per row but the payload merges them) — same limitation as the tag form.
          if (path?.startsWith('overrides[')) {
            const mappedFieldPath = mapAPIListErrorsToFormListErrors(
              'configOverrides',
              path.replace(/^overrides\[/, 'configOverrides[')
            );
            setError(mappedFieldPath, { message }, { shouldFocus: true });
            return true;
          }
          if (path === 'overrides') {
            setError('configOverrides', { message }, { shouldFocus: true });
            return true;
          }
          switch (path) {
            case 'description':
            case 'lighthousesAsRelays':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case 'name':
              setError('networkName', { message }, { shouldFocus: true });
              return true;
            case 'curve':
              setError('curveP256', { message }, { shouldFocus: true });
              return true;
            case 'disableManagedLighthouses':
              setError('useManagedLighthouses', { message }, { shouldFocus: true });
              return true;
            case 'root.global':
            case undefined:
            default:
              setError('root.global', { message });
              return true;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We couldn’t save your changes, please try again' });
      }
      // The save failed, so the form still holds unsaved changes: re-arm the navigation guard.
      setIsCommitting(false);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    // Opting a network out of managed lighthouses is destructive — confirm before saving, whether
    // creating one opted-out (box unchecked) or editing an MTLH-enabled network to opted-out.
    const optingOut =
      !data.useManagedLighthouses && (props.networkId === null || props.defaultValues.useManagedLighthouses);
    if (optingOut) {
      setConfirmDisableMtlh(true);
      return;
    }
    await commitChanges(data);
  });

  const [watchedCidrV4, useManagedLighthouses] = useWatch({
    control,
    name: ['cidrV4', 'useManagedLighthouses'],
  });
  const cidrV4 = watchedCidrV4 ?? '';
  const editCidrV6 = isEditing ? props.defaultValues.cidrV6 : undefined;
  // The saved network's managed lighthouse, joined by networkID from the org-wide
  // GET /v1/managed-lighthouses fetch. Shown only while the box is checked, so an unsaved opt-out
  // doesn't sit next to an "active" readout; a network saved as opted out has no entry (and shows
  // nothing) until the opt-in is actually committed and the refetch lands.
  const { data: managedLighthousesResult } = useQuery({
    ...maxManagedLighthousesQueryOptions,
    enabled: isEditing,
  });
  const editedNetwork = isEditing ? networks.find(({ id }) => id === props.networkId) : undefined;
  const managedLighthouse =
    editedNetwork && useManagedLighthouses && !editedNetwork.disableManagedLighthouses ?
      managedLighthousesResult?.data.find(({ networkID }) => networkID === props.networkId)
    : undefined;
  const valueBeingEdited = editConfigOverrideIndex !== null && configOverrides[editConfigOverrideIndex];

  return (
    <>
      <BlockNavigationDialog when={blockNavigation} title="Unsaved changes">
        You have unsaved changes, are you sure you want to leave?
      </BlockNavigationDialog>
      {confirmDisableMtlh ?
        <DisableManagedLighthousesWarning
          onCancel={() => {
            setConfirmDisableMtlh(false);
          }}
          onAccept={async () => {
            setConfirmDisableMtlh(false);
            await commitChanges(getValues());
          }}
        />
      : null}
      <form action="post" id="network-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="wide">
          {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
          <h2 className="h4 mb-8">Network info</h2>
          <Field label="Name">
            <Input className="width-100" {...register('networkName')} hasError={Boolean(errors.networkName)} />
            {errors.networkName && <ErrorMessage>{errors.networkName.message}</ErrorMessage>}
          </Field>
          <Field label="Description">
            <Input className="width-100" {...register('description')} hasError={Boolean(errors.description)} />
            {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
          </Field>
          {managedLighthousesApplicable ?
            <div>
              <Checkbox
                {...register('useManagedLighthouses')}
                label={
                  <span>
                    Use managed lighthouses
                    <HelpTip>
                      Defined Networking runs and maintains lighthouses for this network, so you don’t have to host your
                      own.
                    </HelpTip>
                  </span>
                }
              />
              {managedLighthouse ?
                managedLighthouse.status === 'active' ?
                  <p className={cn('p-md', styles.NetworkForm_checkboxHint)}>Active</p>
                : <p className={cn('p-md', styles.NetworkForm_checkboxHint)}>
                    Pending — this network’s managed lighthouse is being set up. Hosts will use it automatically once
                    it’s ready.
                  </p>

              : null}
            </div>
          : <div>
              <Checkbox
                disabled
                label={
                  <span>
                    Use managed lighthouses
                    <HelpTip>Managed lighthouses require IPv6 and are not available on this network.</HelpTip>
                  </span>
                }
              />
              {isEditing && props.onOpenUpdate ?
                <p className={cn('p-md', styles.NetworkForm_checkboxHint)}>
                  <InlineButton onClick={props.onOpenUpdate}>Update this network</InlineButton> to add IPv6 and use
                  managed lighthouses.
                </p>
              : null}
            </div>
          }
          <Checkbox
            {...register('lighthousesAsRelays')}
            label={
              <span>
                Use self-hosted lighthouses as relays{' '}
                <Link
                  className="a"
                  to="https://www.defined.net/docs/guides/using-dedicated-relays/"
                  target="_blank"
                  rel="help"
                >
                  Learn more
                </Link>
              </span>
            }
          />
          <Checkbox
            {...register('curveP256')}
            label={
              <span>
                Use P-256 elliptic curve cryptography
                <HelpTip>Cannot be changed after the network is created</HelpTip>
              </span>
            }
            disabled={isEditing}
          />

          <Divider />
          <h2 className="h4 mb-8">IP addresses</h2>

          {!isEditing ?
            <Banner Icon={CheckCircle} iconClassName="color-success">
              IPv6 assigned automatically
            </Banner>
          : null}
          {isEditing && (editCidrV6 || props.onOpenUpdate) ?
            <Field label="IPv6 address range">
              <Input className="width-100" value={editCidrV6} placeholder="None" disabled readOnly />
              {!editCidrV6 && props.onOpenUpdate ?
                <p className="p-md">
                  <InlineButton onClick={props.onOpenUpdate}>Update this network</InlineButton> to enable IPv6 for every
                  host.
                </p>
              : null}
            </Field>
          : null}
          {lockedCidrV4 ?
            <Field label="IPv4 address range">
              <Input className="width-100" value={lockedCidrV4} disabled readOnly />
            </Field>
          : <>
              {!isEditing && cidrV4 ?
                <AssignedIPv6NetworkSize />
              : null}
              <Field id="cidrV4">
                <div className="flex align-center">
                  <Label htmlFor="cidrV4">IPv4 address range (optional)</Label>
                  <HelpTip>
                    Learn more about{' '}
                    <Link
                      className="a"
                      to="https://www.defined.net/docs/get-started/choosing-a-cidr/"
                      target="_blank"
                      rel="help"
                    >
                      choosing an IPv4 CIDR
                    </Link>
                    .
                  </HelpTip>
                </div>
                <HelpText>Use CIDR notation (e.g. 100.100.0.0/22)</HelpText>
                <Input
                  className="width-100"
                  maxLength={256}
                  hasError={Boolean(errors.cidrV4)}
                  {...register('cidrV4')}
                />
                {errors.cidrV4 ?
                  <ErrorMessage>{errors.cidrV4.message}</ErrorMessage>
                : <NetworkSize cidr={cidrV4} />}
                {detectNetworkOverlap(filteredNetworks, cidrV4) ?
                  <p className="p-md">
                    <AlertIcon height={12} width={12} /> This range overlaps with existing networks. It is not possible
                    to enroll a single computer in overlapping networks.
                  </p>
                : null}
              </Field>
            </>
          }

          {showConfigOverrides ?
            <>
              <Divider />
              <FieldSet
                legend="Host advanced config"
                helpText="Optionally apply configuration to every host, lighthouse, and relay in this network. Config set on a tag or an individual host takes precedence."
              >
                {errors.configOverrides?.root && <ErrorMessage>{errors.configOverrides.root.message}</ErrorMessage>}
                {errors.configOverrides?.message && <ErrorMessage>{errors.configOverrides.message}</ErrorMessage>}
                {Array.isArray(errors.configOverrides) ?
                  errors.configOverrides
                    .map((error, index) =>
                      error?.message ?
                        // eslint-disable-next-line react/no-array-index-key -- errors are positional and ephemeral
                        <ErrorMessage key={index}>{error.message}</ErrorMessage>
                      : null
                    )
                    .filter((error) => Boolean(error))
                : null}
                <Field>
                  <Label className="visually-hidden">Host advanced config</Label>
                  <ConfigOverridesList
                    items={configOverrides}
                    onEdit={(index) => {
                      setEditConfigOverrideIndex(index);
                    }}
                    onRemove={removeConfigOverrides}
                    unknownItems={savedUnknownOverrides}
                    removedUnknownIndices={removedUnknownIndices}
                    onRemoveUnknown={removeUnknownOverride}
                    onRestoreUnknown={restoreUnknownOverride}
                  >
                    <AddButton
                      onClick={() => {
                        setIsAddingConfigOverride(true);
                      }}
                    >
                      Add config
                    </AddButton>
                  </ConfigOverridesList>
                </Field>
              </FieldSet>
            </>
          : null}
        </Spacer>
        <ConfirmChangesBanner
          isOpen={isDirty || hasRemovedUnknownOverrides}
          primaryButton={<Button type="submit">Save</Button>}
          secondaryButton={<CancelLinkButton fallbackTo="/settings/networks" />}
        />
      </form>

      {/* Add/Edit modals for config overrides */}
      {isAddingConfigOverride && (
        <AddConfigOverrideModal
          existingOverrides={configOverrides}
          onSave={(values) => {
            appendConfigOverrides(values);
            setIsAddingConfigOverride(false);
          }}
          onCancel={() => {
            setIsAddingConfigOverride(false);
          }}
        />
      )}
      {valueBeingEdited ?
        <EditConfigOverrideModal
          configType={valueBeingEdited.key === 'stats.type' ? valueBeingEdited.value : valueBeingEdited.key}
          value={valueBeingEdited}
          existingOverrides={configOverrides}
          onSave={(values) => {
            if (valueBeingEdited.key === 'stats.type') {
              // Remove all the 'stats' fields, and re-add the new ones
              const statsIndices = configOverrides
                .map((o, i) => (o.key.startsWith('stats.') ? i : null))
                .filter((i): i is number => i !== null);
              removeConfigOverrides(statsIndices);
              appendConfigOverrides(values);
            } else if (values.length === 1 && values[0]) {
              updateConfigOverride(editConfigOverrideIndex, values[0]);
            }
            setEditConfigOverrideIndex(null);
          }}
          onCancel={() => {
            setEditConfigOverrideIndex(null);
          }}
        />
      : null}
      {showRestartModal ?
        <RestartNetworkModal
          networkName={getValues('networkName')}
          onClose={() => {
            props.onSuccess();
            setShowRestartModal(false);
          }}
        />
      : null}
    </>
  );
}
